import { useState, useEffect, useCallback } from 'react';
import {Card, Select, DatePicker, Table, Tag, InputNumber, Col, Row} from 'antd';
import { Button, Loading } from '~/components';
import { reportApi } from '~/api';
import { formatCurrency, handleRequest, apiError } from '~/utils';
import '../style/Report.scss';

const { Option } = Select;

const { RangePicker } = DatePicker;

function RevenueByProductReport()
{
    const [loading, setLoading] = useState(false);

    const [timeRange, setTimeRange] = useState('today');

    const [customDates, setCustomDates] = useState([]);

    const [limit, setLimit] = useState(50);

    const [data, setData] = useState(null);

    const loadReport = useCallback(async () =>
    {
        setLoading(true);

        const params = {
            type: timeRange,
            limit: limit
        };

        if (timeRange === 'custom' && customDates.length === 2)
        {
            params.start_date = customDates[0].format('YYYY-MM-DD');
            params.end_date = customDates[1].format('YYYY-MM-DD');
        }

        const [error, response] = await handleRequest(reportApi.revenueByProduct(params));

        setLoading(false);

        if (!apiError('Lỗi khi tải báo cáo', error, response)) {
            setData(response.data);
        }

    }, [timeRange, customDates, limit]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const columns = [
        {
            title: 'STT',
            key: 'index',
            width: 60,
            align: 'center',
            render: (text, record, index) => index + 1,
        },
        {
            title: 'Mã SP',
            dataIndex: 'product_code',
            key: 'product_code',
            width: 120,
        },
        {
            title: 'Sản phẩm',
            dataIndex: 'product_name',
            key: 'product_name',
            render: (text, record) => (
                <div>
                    <div><strong>{text}</strong></div>
                    {record.attribute_label && (
                        <div style={{ fontSize: 12, color: '#888' }}>{record.attribute_label}</div>
                    )}
                </div>
            ),
        },
        {
            title: 'Đơn vị',
            dataIndex: 'unit',
            key: 'unit',
            width: 80,
            align: 'center',
            render: (value) => value === 'kg' ? '0.1 kg' : 'sp',
        },
        {
            title: 'Số lượng bán',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right',
            width: 120,
            render: (value) => <Tag color="blue">{value}</Tag>,
        },
        {
            title: 'Doanh thu',
            dataIndex: 'subtotal',
            key: 'subtotal',
            align: 'right',
            width: 150,
            render: (value) => <strong style={{ color: '#52c41a' }}>{formatCurrency(value)}</strong>,
        },
        {
            title: 'VAT',
            dataIndex: 'vat',
            key: 'vat',
            align: 'right',
            width: 120,
            render: (value) => formatCurrency(value),
        },
        {
            title: 'Số đơn',
            dataIndex: 'orders_count',
            key: 'orders_count',
            align: 'right',
            width: 100,
        },
    ];

    return (
        <div className="report-container">
            <div className="report-header">
                <h1>Báo cáo Doanh thu theo Sản phẩm</h1>
                <div className="report-filters">
                    <Select
                        value={timeRange}
                        onChange={setTimeRange}
                        style={{ width: 200 }}
                    >
                        <Option value="today">Hôm nay</Option>
                        <Option value="yesterday">Hôm qua</Option>
                        <Option value="this_week">Tuần này</Option>
                        <Option value="last_week">Tuần trước</Option>
                        <Option value="this_month">Tháng này</Option>
                        <Option value="last_month">Tháng trước</Option>
                        <Option value="this_year">Năm nay</Option>
                        <Option value="custom">Tùy chọn</Option>
                    </Select>

                    {timeRange === 'custom' && (
                        <RangePicker
                            value={customDates}
                            onChange={setCustomDates}
                            format="DD/MM/YYYY"
                            style={{ marginLeft: 10 }}
                        />
                    )}

                    <InputNumber
                        value={limit}
                        onChange={setLimit}
                        min={10}
                        max={500}
                        step={10}
                        style={{ marginLeft: 10, width: 100 }}
                        addonAfter="SP"
                    />

                    <Button
                        primary
                        onClick={loadReport}
                        style={{ marginLeft: 10 }}
                        disabled={timeRange === 'custom' && customDates.length !== 2}
                    >
                        Xem báo cáo
                    </Button>
                </div>
            </div>

            {loading && <Loading />}

            {!loading && data && (
                <>
                    <Card title={`Top ${data.data?.length} sản phẩm bán chạy`} className="table-card">
                        <Table
                            columns={columns}
                            dataSource={data.details}
                            rowKey={(record) => record.product_id || record.product_code}
                            pagination={{
                                pageSize: 20,
                                showSizeChanger: true,
                                showTotal: (total) => `Tổng ${total} sản phẩm`
                            }}
                            scroll={{ x: 1200 }}
                        />
                    </Card>
                </>
            )}
        </div>
    );
}

export default RevenueByProductReport;
